import { useMemo, useState } from 'react';

import { Box } from '@aicon-dev/caas-core';
import { Button, CascadePickerView, Divider, NavBar, Popup } from 'antd-mobile';
import { PickerValue } from 'antd-mobile/es/components/picker-view';
import { uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useModalState } from '~/libs/hooks/useModalState';
import { useProjectContext } from '~/modules/projects/context/ProjectProvider';

import { getFilterProcessTagInfo, getProcessText } from './ReportListFilter.helper';

export interface ProcessSelectProps {
  value: string[];
  onChange?: (value: string[]) => void;
}

export const ProcessSelect = (props: ProcessSelectProps) => {
  const { value, onChange } = props;
  const { t } = useTranslation(['global', 'report']);
  const [visible, setVisible] = useModalState();
  const { project } = useProjectContext();
  const [_temps, _setTemps] = useState<PickerValue[]>([]);

  const parsedValue = value || [];

  /**
   * 1차원으로 정렬된 공정을 3차원 배열로 구성합니다.
   * 이때 이미 선택된 공정 또는 세부공정은 표시하지 않습니다.
   */
  const options = useMemo(() => {
    const processes = project?.processes || [];
    return [
      // {
      //     label: (
      //         <Box color="#cdcdcd" fontWeight={400}>
      //             공정 그룹 선택
      //         </Box>
      //     ),
      //     value: '',
      // },
      ...uniqBy(processes, 'group_id').map((item) => {
        const matchedProcesses = processes.filter((e) => e.group_id === item.group_id);
        return {
          label: item.group,
          value: item.group_id || '',
          children: [
            // {
            //     label: (
            //         <Box color="#cdcdcd" fontWeight={400}>
            //             공정 선택
            //         </Box>
            //     ),
            //     value: '',
            // },
            ...matchedProcesses
              .filter((p) => !parsedValue?.find((id) => id === p._id))
              .map((p) => {
                return {
                  label: p.title,
                  value: p._id,
                  children: [
                    {
                      label: (
                        <Box color="#cdcdcd" fontWeight={400}>
                          {/* 세부 공정 선택 */}
                        </Box>
                      ),
                      value: p._id,
                    },
                    ...p.tasks
                      .filter((t) => !parsedValue?.find((id) => id === t._id))
                      .map((t) => ({
                        label: t.text,
                        value: t._id,
                      })),
                  ],
                };
              }),
          ],
        };
      }),
    ];
  }, [project?.processes, parsedValue]);

  const handleRemoveTag = (id: string) => {
    onChange && onChange(parsedValue.filter((e) => e !== id) || []);
  };

  const handleConfirm = (result: PickerValue[]) => {
    if (!onChange) return;
    const processId = result?.[1] as string;
    const taskId = result?.[2] as string;

    // 공정을 아직 선택하지 않았을 경우
    if (!processId) return;

    const isAlreadyExist = !!parsedValue?.find((id) => id === processId || id === taskId);
    console.log('hi', isAlreadyExist);
    // 이미 존재하는 경우
    if (isAlreadyExist) return;

    // 공정만 선택한 경우
    if (!taskId) {
      onChange([...parsedValue, processId]);
      setVisible(false);
      return;
    }

    // 세부공정까지 선택한 경우
    if (taskId) {
      onChange([...parsedValue, taskId]);
      setVisible(false);
    }
  };

  return (
    <>
      <Popup
        closeOnMaskClick
        destroyOnClose
        visible={visible}
        onClose={() => setVisible(false)}
        bodyStyle={{
          minHeight: '240px',
        }}
      >
        <NavBar
          back={
            <Button size="small" onClick={() => setVisible(false)}>
              {t('global:m_button_close')}
            </Button>
          }
          backArrow={false}
          right={
            <Button
              disabled={!_temps?.[1]}
              color="primary"
              size="small"
              onClick={() => {
                handleConfirm(_temps);
              }}
            >
              {t('global:m_button_done')}
            </Button>
          }
        ></NavBar>
        <Divider
          style={{
            margin: 0,
          }}
        />
        <Box display="flex" py="0.5rem" color="#aeaeae">
          <Box flex={1} fontSize="1rem" fontWeight={400} textAlign="center">
            {t('report:m_filter_process_group')}
          </Box>
          <Box flex={1} fontSize="1rem" fontWeight={400} textAlign="center">
            {t('report:m_filter_process')}
          </Box>
          <Box flex={1} fontSize="1rem" fontWeight={400} textAlign="center">
            {t('report:m_filter_task')}
          </Box>
        </Box>
        {/* <Divider
                    style={{
                        margin: 0,
                    }}
                /> */}
        <CascadePickerView
          value={_temps}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          options={options as any}
          onChange={(value) => _setTemps(value)}
        />
      </Popup>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pb="10px"
        pr="14px"
        borderBottom="1px solid #EEEEEE"
        onClick={() => setVisible(true)}
      >
        <Box color="#CCCCCC" fontSize="1rem">
          {t('report:filter_process_input_placeholder')}
        </Box>

        <svg
          width="17"
          height="17"
          viewBox="0 0 17 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <mask id="mask0_146_19886" maskUnits="userSpaceOnUse" x="1" y="1" width="15" height="15">
            <path d="M16 1L1 1L0.999999 16L16 16L16 1Z" fill="white" />
          </mask>
          <g mask="url(#mask0_146_19886)">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M8.42606 10.8818L2.24306 4.69777C2.10634 4.56105 1.92091 4.48424 1.72756 4.48424C1.53421 4.48424 1.34878 4.56105 1.21206 4.69777C1.07534 4.83449 0.998535 5.01992 0.998535 5.21327C0.998535 5.40662 1.07534 5.59205 1.21206 5.72877L7.93506 12.4458C8.01556 12.5257 8.11355 12.5858 8.22129 12.6213C8.32902 12.6568 8.44355 12.6668 8.55579 12.6504C8.66804 12.634 8.77493 12.5917 8.86801 12.5269C8.96109 12.462 9.03779 12.3764 9.09206 12.2768L15.7921 5.57677C15.9215 5.43822 15.9919 5.25469 15.9882 5.06512C15.9846 4.87555 15.9072 4.69486 15.7726 4.56137C15.6379 4.42789 15.4566 4.35212 15.267 4.35014C15.0774 4.34816 14.8945 4.42013 14.7571 4.55077L8.42606 10.8818Z"
              fill="#404040"
            />
          </g>
        </svg>
      </Box>

      <TagList>
        {value?.map((id) => {
          const tagInfo = getFilterProcessTagInfo(id, project?.processes);
          return (
            <TagItemBox key={id} onClick={() => handleRemoveTag(id)}>
              <span>{getProcessText(tagInfo?.processName, tagInfo?.taskName)}</span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="6" cy="6" r="6" fill="#C8CACC" />
                <path
                  d="M3.66308 8.77833L8.78019 3.66121C8.90186 3.53955 8.90186 3.34229 8.78019 3.22062L8.77991 3.22034C8.65825 3.09867 8.46099 3.09867 8.33932 3.22034L3.2222 8.33746C3.10054 8.45912 3.10054 8.65638 3.2222 8.77805L3.22249 8.77833C3.34415 8.9 3.54141 8.9 3.66308 8.77833Z"
                  fill="white"
                />
                <path
                  d="M8.77833 8.3379L3.66121 3.22078C3.53955 3.09912 3.34229 3.09912 3.22062 3.22078L3.22034 3.22107C3.09867 3.34273 3.09867 3.53999 3.22034 3.66166L8.33746 8.77877C8.45912 8.90044 8.65638 8.90044 8.77805 8.77877L8.77833 8.77849C8.9 8.65683 8.9 8.45957 8.77833 8.3379Z"
                  fill="white"
                />
              </svg>
            </TagItemBox>
          );
        })}
      </TagList>
    </>
  );
};

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

const TagItemBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  height: 28px;

  color: #414c5c;
  font-weight: 500;
  font-size: 14px;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 4px;
`;
